import { Selector } from "react-redux";
import { createSelector } from "reselect";

import { EMPTY_ARRAY } from "@multiweb/reactUtils";
import { MenuItemType } from "@multiweb/types/contracts";
import { IAppState, TypedObject } from "@multiweb/types/react";
import { getSearchResults, isSearchActive } from "../search";
import {
    getActiveSectionFormItemId,
    getFormChildIds,
    getFormItem,
    getFormItems,
    getNotificationItemIds,
    getPanelItem,
} from "../selectors";
import { MenuPanelItemType } from "../types";
import { createItemIdForProfile } from "../utils";
import {
    FocusableActionItemTypes,
    FocusableItemId,
    IsItemFocusedSelectorParams,
    MenuFocusableItemType,
    MenuNavigationSectionType,
    NavigationNode,
    NavigationSection,
} from "./types";
import { collapseMenuNavigationSectionType, createFocusableItemId, focusableItemTypeToSearchResultType, searchGroupToNavigationSection } from "./utils";

/**
 * Získá z globálního stavu podstrom pro navigaci pomocí klávesnice v menu.
 */
const getModel = (state: IAppState) => state.menu.navigation;

/**
 * Získá navigační uzel podle id.
 * @param state
 * @param focusableItemId Id zaměřitelné položky menu, pro kterou se získá navigační uzel.
 */
export const getNavigationNode: Selector<IAppState, NavigationNode | undefined, FocusableItemId> = (state, focusableItemId) => {
    const navigationNodes = getModel(state).navigationNodes;
    for (const section of Object.keys(navigationNodes)) {
        const matchingNode = navigationNodes[collapseMenuNavigationSectionType(section)][focusableItemId];
        if (matchingNode) {
            return matchingNode;
        }
    }
    return undefined;
};

/**
 * Získá navigační sekci, ve které je uložený uzel aktuálně zaměřené položky.
 * @param state
 */
export const getFocusedItemStoreSection: Selector<IAppState, NavigationSection | undefined> = (state) => {
    const focusedItemId = getFocusedItemId(state);
    if (!focusedItemId) {
        return undefined;
    }
    const navigationNodes = getModel(state).navigationNodes;
    for (const section of Object.keys(navigationNodes)) {
        const searchedSection = collapseMenuNavigationSectionType(section);
        const matchingNode = navigationNodes[searchedSection][focusedItemId];
        if (matchingNode) {
            return searchedSection;
        }
    }
    return undefined;
};

/**
 * Získá navigační uzel pro aktuálně zaměřenou položku.
 * @param state
 */
export const getFocusedNavigationNode: Selector<IAppState, NavigationNode | undefined> = (state) => {
    const focusedItemId = getFocusedItemId(state);
    return focusedItemId ? getNavigationNode(state, focusedItemId) : undefined;
};

/**
 * Získá navigační uzel pro položku sekce podle jejího id. Podporuje i notifikační skupiny.
 * @param state
 * @param sectionId Id sekce nebo notifikační skupiny.
 */
export const getSectionNavigationNode: Selector<IAppState, NavigationNode | undefined, string> = (state, sectionId) => {
    const panelItem = getPanelItem(state, sectionId);
    if (!panelItem) {
        return undefined;
    }

    const section = getSectionOfPanelItemId(state, sectionId);
    if (!section) {
        return undefined;
    }

    return getNavigationNode(state, createFocusableItemId(sectionId, MenuFocusableItemType.panelItem, section));
};

/**
 * Získá navigační sekci, do které patří položka panelu menu se zadaným id.
 * @param state
 * @param panelItemId Id položky panelu menu.
 */
export const getSectionOfPanelItemId: Selector<IAppState, MenuNavigationSectionType | undefined, string> = (state, panelItemId) => {
    const panelItem = getPanelItem(state, panelItemId);
    if (!panelItem) {
        return undefined;
    }
    if (panelItem.type === MenuPanelItemType.notificationGroup) {
        return MenuNavigationSectionType.notificationGroups;
    } else if (panelItem.type === MenuPanelItemType.form) {
        return MenuNavigationSectionType.panelForms;
    } else {
        return MenuNavigationSectionType.static;
    }
};

/**
 * Získá id zaměřitelné položky, která je aktuálně zaměřená.
 * @param state
 */
export const getFocusedItemId: Selector<IAppState, FocusableItemId | null> = (state) => getModel(state).focusedItemId;

/**
 * Zjistí, zda je zaměřitelná položka aktuálně zaměřená.
 * @param state
 * @param focusableItemId Id kontrolované zaměřitelné položky.
 */
export const isFocused: Selector<IAppState, boolean, FocusableItemId> = (state, focusableItemId) => getFocusedItemId(state) === focusableItemId;

/**
 * Zjistí, zda je položka daného typu aktuálně zaměřena.
 * @param state
 * @param itemId Id položky.
 * @param type Typ položky.
 * @param section Sekce položky.
 */
export const isItemFocused: Selector<IAppState, boolean, IsItemFocusedSelectorParams> = (state, { itemId, type, section }) =>
    isFocused(state, createFocusableItemId(itemId, type, section));

/**
 * Zjisti, zda je položka formuláře nebo složky formulářů zaměřená.
 * @param state
 * @param formItemId Id položky formuláře.
 * @param section Sekce položky.
 */
export const isFormItemFocused: Selector<IAppState, boolean, {formItemId: number; section: NavigationSection}> = (state, { formItemId, section }) => {
    const formItem = getFormItem(state, formItemId);
    if (formItem) {
        const type = formItem.type === MenuItemType.form ? MenuFocusableItemType.formItem : MenuFocusableItemType.folderItem;
        return isItemFocused(state, { itemId: formItemId, type, section });
    } else {
        return false;
    }
};

/**
 * Zjistí, zda je zaměřená některá z akčních položek patřící k položce se zadaným id.
 * @param state
 * @param parentItemId Id rodičovské položky akční položky.
 * @param section Sekce, ve které se nachází rodičovská položka.
 */
export const isActionItemFocused: Selector<IAppState, boolean, {parentItemId: number | string; section: NavigationSection}> = (state, { parentItemId, section }) => {
    const focusedNavigationNode = getFocusedNavigationNode(state);
    return !!focusedNavigationNode &&
        parentItemId === focusedNavigationNode.self.itemId &&
        section === focusedNavigationNode.self.section &&
        FocusableActionItemTypes.includes(focusedNavigationNode.self.type);
};

/**
 * Získá index zaměřené položky výsledků vyhledávání nebo formuláře z menu.
 * @param state
 */
export const getFocusedFormItemIndex: Selector<IAppState, number | undefined> = (state) => {
    const focusedNode = getFocusedNavigationNode(state);
    if (!focusedNode) {
        return undefined;
    }
    if (isSearchActive(state)) {
        return getFocusedSearchResultIndex(state, focusedNode);
    } else if (focusedNode.self.section === MenuNavigationSectionType.forms) {
        const activeSectionFormItemId = getActiveSectionFormItemId(state);
        if (!activeSectionFormItemId) {
            return undefined;
        }
        return getMenuFormItemIndexMap(state, activeSectionFormItemId)[focusedNode.self.itemId];
    } else {
        return undefined;
    }
};

/**
 * Pomocný cachovaný selector pro tvorbu indexové mapy složené z formulářů a jejich profilů. Profily musí mít shodný index s rodičovským formulářem, protože
 * se vykreslují v rámci stejné položky ve virtualizovaném listu.
 */
const getMenuFormItemIndexMap = createSelector<IAppState, number, ReturnType<typeof getFormChildIds>, ReturnType<typeof getFormItems>, TypedObject<number>>(
    (state, activeSectionFormItemId) => getFormChildIds(state, activeSectionFormItemId),
    getFormItems,
    (menuFormsIds, formItems) => menuFormsIds.reduce((indexMap, formId, index) => {
        const profileIds = formItems[formId]?.profiles ?? EMPTY_ARRAY;
        const profileIndexMap = profileIds.reduce((profileIndexMap, profileId) => {
            return {
                ...profileIndexMap,
                [createItemIdForProfile(profileId, formId)]: index,
            };
        }, {});
        return {
            ...indexMap,
            [formId]: index,
            ...profileIndexMap,
        };
    }, {}),
);

/**
 * Pomocný cachovaný selector pro získání indexu pro selector getFocusedFormItemIndex v případě, že je aktivní vyhledávání.
 */
const getFocusedSearchResultIndex = createSelector<IAppState, NavigationNode, NavigationNode, ReturnType<typeof getSearchResults>, number | undefined>(
    (_state, focusedNode) => focusedNode,
    getSearchResults,
    (focusedNode, searchResults) => {
        const { itemId: focusedItemId, type: focusedNodeType, section } = focusedNode.self;
        const focusedNodeSearchResultType = focusableItemTypeToSearchResultType(focusedNodeType);
        const index = searchResults.findIndex(({ id, type, searchGroup }) =>
            id === focusedItemId &&
            type === focusedNodeSearchResultType &&
            section === searchGroupToNavigationSection(searchGroup),
        );
        return index === -1 ? undefined : index;
    },
);

/**
 * Získá index zaměřené položky notifikace, pokud existuje.
 * @param state
 *
 * Poznámka pro vývojáře:
 * Selector nemůže používat navigační uzly, protože při aktualizaci notifikací se nejdříve všechny smažou, než se nastaví nové.
 * To pak vede k vrácení undefined v době aktualizace. To má pak za následek nechtěné překreslení položky ve virtualizovaném listu.
 */
export const getFocusedNotificationIndex: Selector<IAppState, number | undefined> = createSelector(
    getFocusedItemId,
    getNotificationItemIds,
    (focusedItemId, notificationIds) => {
        if (focusedItemId === null) {
            return undefined;
        }
        const focusableNotificationIds = notificationIds.map((notificationId) => createFocusableItemId(notificationId, MenuFocusableItemType.notificationItem, MenuNavigationSectionType.notifications));
        const index = focusableNotificationIds.indexOf(focusedItemId);
        return index === -1 ? undefined : index;
    },
);

/**
 * Získá id první zaměřitelné položky ze sekce Rychlý přístup (bez aktivního vyhledávání).
 * @param state
 */
export const getFirstQuickAccessFocusableItemId: Selector<IAppState, FocusableItemId | undefined> = (state) => getModel(state).firstQuickAccessFocusableItemId ?? undefined;

/**
 * Získá id poslední zaměřitelné položky ze sekce Rychlý přístup (bez aktivního vyhledávání).
 * @param state
 */
export const getLastQuickAccessFocusableItemId: Selector<IAppState, FocusableItemId | undefined> = (state) => getModel(state).lastQuickAccessFocusableItemId ?? undefined;

/**
 * Získá příznak, zda se má při zaměření vyhledávacího pole označit celý text. Řeší případ, kdy se po napsání prvního písmene vymění komponenty a tím se nechtěně
 * označí text.
 * @param state
 */
export const getSelectAllTextInSearchInput: Selector<IAppState, boolean> = (state) => getModel(state).searchInputSelectAllText;
